import React from 'react'
import {QueryResponse} from 'types/graphql'
import {createClient} from 'types/graphql/createClient'
import {Query, QueryRequest} from 'types/graphql/schema'
import {CenteredSpinner} from 'components/CenteredSpinner'


export class GraphqlTransportError extends Error {
  constructor(readonly status: number, readonly statusText: string, readonly bodyPreview: string) {
    super(`GraphQL request failed: ${status} ${statusText} — response body was not JSON`)
    this.name = 'GraphqlTransportError'
    Object.setPrototypeOf(this, GraphqlTransportError.prototype)
  }
}

export const graphqlClient = createClient({
  fetcher: async (query, fetch) => {
    const response = await fetch(
      `/gql-api`,
      {
        method: 'POST',
        body: JSON.stringify(query),
        headers: {
          'X-CSRFToken': window.GLOBAL_CONFIG.CSRF,
          'sessionid': window.GLOBAL_CONFIG.SESSION,
          'Content-Type': 'application/json',
        },
      },
    )

    // Parse regardless of status: graphene-django returns a valid GraphQL envelope
    // with 400 for things like malformed queries, and those must stay on the
    // normal `errors` path rather than becoming transport failures.
    const body = await response.text()
    try {
      return JSON.parse(body)
    } catch {
      throw new GraphqlTransportError(response.status, response.statusText, body.slice(0, 200))
    }
  },
})

type ErrorExtensions = {
  common_errors?: string[]
  field_errors?: Record<string, string>
  error_code?: string
}

/**
 * Pull the `extensions` payload off a rejected graphql-typed-client call, or rethrow.
 *
 * Only one of the library's throw sites populates `.errors` (the "Response contains
 * errors" one); "Response data is empty" and "Response path is empty" leave it
 * undefined, and a network abort or non-JSON response rejects with a plain
 * TypeError/GraphqlTransportError that has no `.errors` at all. Reaching straight for
 * `e.errors[0]` therefore throws inside the catch handler and masks the real failure.
 *
 * Anything that isn't a backend-reported GraphQL error is rethrown **as-is** — same
 * type, message and stack — so Sentry reports the actual fault rather than a
 * TypeError from this file. Rethrowing here rather than returning a sentinel means a
 * caller cannot accidentally swallow it.
 */
export function requireErrorExtensions(e: unknown): ErrorExtensions {
  const extensions = (e as {errors?: {extensions?: ErrorExtensions}[]})?.errors?.[0]?.extensions
  if (!extensions) throw e
  return extensions
}

export type UseQueryBaseReturnType = {
  errors: QueryResponse['errors']
  /**
   * Set when the query could not be loaded at all — aborted fetch, dropped
   * connection, non-JSON response. Distinct from `errors`, which carries a
   * response the backend did produce. Callers must treat this as "unknown",
   * not as "no data": rendering the logged-out path on a failed currentUser
   * query would sign a user out on any network blip.
   */
  failure?: unknown
  isFetching: boolean
  refetch: React.DispatchWithoutAction
  spinner: JSX.Element | null
}

export function useQuery(query: QueryRequest): UseQueryBaseReturnType & { data: Query }
export function useQuery<F extends keyof Query>(query: QueryRequest, field: F): UseQueryBaseReturnType & {
  data: Query[F]
}
export function useQuery<F extends keyof Query>(query: QueryRequest, field?: F) {
  type dataType = F extends keyof Query ? Query[F] : Query
  const [data, setData] = React.useState<dataType>()
  const [errors, setErrors] = React.useState<QueryResponse['errors']>()
  const [failure, setFailure] = React.useState<unknown>()
  const [isFetching, setIsFetching] = React.useState(false)
  const [hasResponse, setHasResponse] = React.useState(false)
  const [spinner, setSpinner] = React.useState<JSX.Element | null>(<CenteredSpinner/>)

  const refetch = React.useReducer(() => {
    setHasResponse(false)
    setErrors(undefined)
    setFailure(undefined)
  }, undefined)[1]

  React.useEffect(() => {
    if (!hasResponse && !isFetching) {
      setIsFetching(true)
      graphqlClient
        .query(query)
        .then(response => {
          setErrors(response.errors)
          let resData
          if (field && response.data) {
            resData = (response.data as any)[field] as dataType
          } else {
            resData = response.data as unknown as dataType
          }
          response.data ? setData(resData) : setData(undefined)
          setHasResponse(true)
          setIsFetching(false)
        })
        // Without this the hook latches: isFetching is only ever cleared in the
        // .then above, so a rejected query left it true forever. The fetch
        // effect is guarded on `!isFetching`, so nothing re-fired — not even
        // refetch() — and the caller was stuck rendering a spinner with no way
        // out. Clearing both flags is what makes the retry button possible.
        .catch(e => {
          setFailure(e)
          setData(undefined)
          setHasResponse(true)
          setIsFetching(false)
        })
    }
  }, [hasResponse, isFetching])

  React.useEffect(() => {
    if ((!data && isFetching) || !hasResponse) {
      setSpinner(<CenteredSpinner/>)
    } else {
      setSpinner(null)
    }
  }, [data, isFetching, hasResponse, errors])

  if (errors && errors.length) console.warn(errors)

  return {
    data,
    errors,
    failure,
    isFetching,
    refetch,
    spinner,
  }
}